/**
 * FOUNDATIONS & GEOTECHNICAL model — self-contained capacity formulas that turn
 * the ground into a real, defeatable part of the structure. Right now the solver
 * treats base rows and guy anchors as infinitely rigid (invMass 0). This module
 * gives each of those a *finite* capacity computed from {foundation geometry,
 * concrete grade, soil}, so a base leg can lift (uplift/pullout) or the pad can
 * punch into soft soil (bearing/overturning) → "Fall Over" or "Get Sued".
 *
 * Units are SI throughout: metres, Newtons, Pascals, kg/m^3, N·m. Angles in
 * radians unless a field name says "Deg".
 *
 * NOT yet wired into the solver — see docs/FOUNDATIONS.md §7 for integration.
 * Standalone by design: only depends on stable numeric types, nothing that
 * another agent is actively editing.
 *
 * Sources (see docs/FOUNDATIONS.md for the full discussion):
 *  - [ACI] Telecom Tower RC Foundation, ACI 318-14 worked example (StructurePoint
 *    spMats): 100 ft lattice, 10×10×1.5 ft pad + 5.5 ft pier, f'c = 3000 psi,
 *    fy = 60000 psi, sandy soil qa = 2.0 ksf (~96 kPa), M_wind = 150 kip·ft.
 *  - [TEP] WA Oak Harbor 170 ft monopole subsurface report (Tower Engineering
 *    Professionals, 2023): SM/GM dense sand, φ = 43–45°, γ' = 114–118 pcf
 *    (18.0–18.5 kN/m³), sliding friction factor 0.50, drilled-shaft side friction.
 *  - [HM] 16 m high-mast foundation calc (Bajaj/Plateformes, Congo): pad+pedestal
 *    soil-pressure check Pmax = P/(A) ± M/Z with Z = L·B²/6, overturning FS =
 *    M_resist/M_over = 2.25 > 1.50, concrete 2.5 T/m³, M-20, Fe-500.
 *  - [TIA] TIA-222-G/H foundation safety factors; Terzaghi bearing theory.
 */

// --- physical constants ---

/** Gravitational acceleration (m/s^2). */
const G = 9.81;
/** Density of reinforced concrete (kg/m^3). ~2500 → 24.5 kN/m^3. [HM] */
export const CONCRETE_DENSITY = 2500;

// ---------------------------------------------------------------------------
// Concrete grade
// ---------------------------------------------------------------------------

/**
 * Concrete compressive strength grade. f'c is the 28-day cylinder strength (Pa).
 * Footings/piers are mass concrete: the *soil* almost always governs bearing, so
 * f'c mostly sets pier/pedestal punching + reinforcement need, and (in-game) a
 * cost/quality knob. Typical tower concrete is 20–40 MPa (3000–6000 psi). [ACI][HM]
 */
export interface ConcreteGrade {
  name: string;
  fc: number; // f'c, characteristic compressive strength (Pa)
  fy: number; // reinforcement yield (Pa) — rebar grade
}

export const CONCRETE_GRADES = {
  // M20 / ~3000 psi — the workhorse tower-footing mix. [ACI f'c=3000psi][HM M-20]
  c20: { name: "20 MPa (M20 / 3000 psi)", fc: 20e6, fy: 420e6 },
  // M25 — common upgrade.
  c25: { name: "25 MPa (M25)", fc: 25e6, fy: 420e6 },
  // M30 / ~4350 psi — piers/drilled shafts.
  c30: { name: "30 MPa (M30 / 4350 psi)", fc: 30e6, fy: 500e6 },
  // M40 / ~5800 psi — heavy monopole shafts, high-strength. [ACI upper end]
  c40: { name: "40 MPa (M40 / 5800 psi)", fc: 40e6, fy: 500e6 },
} satisfies Record<string, ConcreteGrade>;

export type ConcreteGradeKey = keyof typeof CONCRETE_GRADES;

/** Sensible default so existing towers keep working: the workhorse 20 MPa mix. */
export const DEFAULT_CONCRETE: ConcreteGradeKey = "c20";

// ---------------------------------------------------------------------------
// Soil
// ---------------------------------------------------------------------------

/**
 * Geotechnical parameters for a soil preset. These drive bearing (download),
 * uplift (soil cone pullout), and sliding (base friction / passive).
 *
 *  - qa    allowable net bearing capacity (Pa). What a spread footing may press
 *          on the soil at working loads. This is the headline "how strong is the
 *          ground" number. Ranges below are standard geotech tables. [ACI][TEP]
 *  - gamma effective unit weight (N/m^3). Weight of a soil cone resisting uplift
 *          and of overburden. ~18 kN/m^3 dense sand, ~16 soft. [TEP 18.0–18.5]
 *  - phiDeg drained friction angle (degrees). Sets sliding + uplift cone spread.
 *          Sand 30–45°, clay ~0 (drained). [TEP 43–45° dense sand]
 *  - cohesion  undrained shear strength c (Pa). Clays have it, sands ~0. Governs
 *          bearing of clay (qu ≈ 5.14·c) and its uplift. [Terzaghi]
 *  - frictionFactor  base sliding coefficient tan(δ) between concrete & soil.
 *          ~0.5 for sand/silt on concrete. [TEP 0.50]
 */
export interface SoilParams {
  name: string;
  qa: number; // allowable bearing capacity (Pa)
  gamma: number; // effective unit weight (N/m^3)
  phiDeg: number; // friction angle (degrees)
  cohesion: number; // undrained cohesion c (Pa)
  frictionFactor: number; // base sliding coefficient
}

/**
 * Soil presets, softest → hardest. qa values are conventional allowable-bearing
 * ranges (kPa): soft clay ~75–150, stiff clay ~150–300, loose sand ~100–150,
 * dense sand ~300–500+, gravel ~400–600, rock ~1000–5000+. We take one
 * representative value per preset. γ, φ, c from the same tables and the TEP
 * report (dense sand φ≈43°, γ≈18 kN/m^3). Rock: huge qa, treated as effectively
 * unbeatable so a well-founded tower on rock never loses at the ground.
 */
export const SOIL_PRESETS = {
  "soft-clay": {
    name: "Soft clay",
    qa: 100e3, // 100 kPa (75–150 range)
    gamma: 16e3, // 16 kN/m^3
    phiDeg: 0, // undrained clay: friction ~0
    cohesion: 30e3, // c ≈ qa/3 for soft clay (qu = 5.14c)
    frictionFactor: 0.35,
  },
  "stiff-clay": {
    name: "Stiff clay",
    qa: 200e3, // 200 kPa (150–300)
    gamma: 18e3,
    phiDeg: 0,
    cohesion: 75e3, // c ≈ 75 kPa
    frictionFactor: 0.4,
  },
  "loose-sand": {
    name: "Loose sand",
    qa: 125e3, // 125 kPa (100–150)
    gamma: 16e3,
    phiDeg: 30, // loose
    cohesion: 0,
    frictionFactor: 0.45,
  },
  "dense-sand": {
    name: "Dense sand / gravel",
    qa: 400e3, // 400 kPa (300–600); TEP site was much higher (ultimate)
    gamma: 18.5e3, // 118 pcf ≈ 18.5 kN/m^3 [TEP]
    phiDeg: 43, // [TEP dense SM/GM 43–45°]
    cohesion: 0,
    frictionFactor: 0.5, // [TEP 0.50]
  },
  rock: {
    name: "Rock",
    qa: 3000e3, // 3 MPa (competent rock 1–5+ MPa) — effectively unbeatable
    gamma: 24e3,
    phiDeg: 45,
    cohesion: 500e3,
    frictionFactor: 0.7,
  },
} satisfies Record<string, SoilParams>;

export type SoilType = keyof typeof SOIL_PRESETS;

/** Default soil: a competent dense sand so existing towers don't regress. */
export const DEFAULT_SOIL: SoilType = "dense-sand";

// ---------------------------------------------------------------------------
// Foundation spec
// ---------------------------------------------------------------------------

/** Foundation family. Each resists the base reactions differently. */
export type FoundationType =
  | "pad" // spread/pad footing (+ pedestal) — self-support & guyed base
  | "pier" // drilled pier / caisson — monopoles, small legs
  | "deadman" // buried concrete block guy anchor — resists uplift by weight
  | "rock-anchor"; // grouted / rock / helical anchor — resists uplift by bond

/**
 * A single foundation's geometry + material. One of these per fixed base row (or
 * per leg) and per guy-anchor ring. Kept deliberately minimal: a type, a couple
 * of sizes, and a concrete grade — presets + a few knobs, not a CAD tool.
 *
 * Sizes are per foundation element:
 *  - pad:    width (square side, m) and depth (embedment to underside, m).
 *  - pier:   diameter (m) and depth (m).
 *  - deadman: width (block side, m) and depth (burial to top of block, m).
 *  - rock-anchor: diameter (bore, m) and depth (bonded length, m).
 */
export interface FoundationSpec {
  type: FoundationType;
  width: number; // square footing side OR block side (m); use for pads/deadmen
  diameter: number; // pier / anchor bore diameter (m); use for piers/anchors
  depth: number; // embedment / burial / bonded depth (m)
  concrete: ConcreteGradeKey;
}

/** Competent default footing so existing (foundation-less) towers keep standing. */
export const DEFAULT_FOUNDATION: FoundationSpec = {
  type: "pad",
  width: 2.0, // 2 m square pad
  diameter: 0.6, // (unused for pad, but a valid pier default)
  depth: 1.5, // 1.5 m embedment [ACI pad 1.5 ft thk + pier; HM 1.4 m]
  concrete: DEFAULT_CONCRETE,
};

// ---------------------------------------------------------------------------
// Derived geometry helpers
// ---------------------------------------------------------------------------

/** Bearing plan area A (m^2) of a foundation element. */
export function bearingArea(f: FoundationSpec): number {
  if (f.type === "pier" || f.type === "rock-anchor") {
    return Math.PI * (f.diameter * 0.5) * (f.diameter * 0.5);
  }
  return f.width * f.width; // pad / deadman: square
}

/** Concrete volume (m^3) — pad/pier/block as a prism/cylinder of `depth`. */
export function concreteVolume(f: FoundationSpec): number {
  return bearingArea(f) * f.depth;
}

/** Self-weight of the concrete foundation (N). Resists uplift & overturning. */
export function selfWeight(f: FoundationSpec): number {
  return concreteVolume(f) * CONCRETE_DENSITY * G;
}

// ---------------------------------------------------------------------------
// Capacities — all return Newtons (forces) or N·m (moments)
// ---------------------------------------------------------------------------

/**
 * BEARING (download) capacity — max compression the ground can take before the
 * footing punches in (N). Simplified from Terzaghi
 *   qu = c·Nc + q·Nq + 0.5·γ·B·Nγ
 * to the engineering shortcut used in practice: allowable bearing qa (already a
 * safe, factored soil pressure) × plan area, plus the surcharge from embedment
 * (γ·depth) which raises capacity for deeper footings.
 *
 *   P_bear = (qa + γ·depth) · A          [ACI/HM soil-pressure check]
 *
 * Piers/shafts additionally get side friction (see `pileFriction`), but for the
 * game the end-bearing term dominates and is enough.
 */
export function bearingCapacity(f: FoundationSpec, soil: SoilParams): number {
  const A = bearingArea(f);
  const surcharge = soil.gamma * f.depth; // overburden raises bearing
  return (soil.qa + surcharge) * A;
}

/**
 * Skin/side friction for a pier or drilled shaft (N). Shaft resists axial load
 * (both download and uplift) by shear on its cylindrical surface:
 *   Q_s = π·D·L · f_s,  with f_s ≈ K·σ'v·tanφ + adhesion.
 * We use a depth-averaged effective vertical stress σ'v = 0.5·γ·depth and a
 * lateral earth-pressure coefficient K ≈ 1 - sinφ (Jaky). [TEP side-friction table]
 */
export function pileFriction(f: FoundationSpec, soil: SoilParams): number {
  const phi = (soil.phiDeg * Math.PI) / 180;
  const perimeter = Math.PI * f.diameter;
  const K = 1 - Math.sin(phi); // at-rest earth pressure (Jaky)
  const sigmaV = 0.5 * soil.gamma * f.depth; // avg effective vertical stress
  const fsFriction = K * sigmaV * Math.tan(phi);
  const fsAdhesion = 0.5 * soil.cohesion; // adhesion factor α≈0.5 for clay
  return perimeter * f.depth * (fsFriction + fsAdhesion);
}

/**
 * UPLIFT / PULLOUT capacity — max tension a foundation can resist before the
 * leg/anchor pulls out of the ground (N). This is THE classic guyed-mast and
 * 4-legged failure: the windward leg/anchor lifts. Two mechanisms:
 *
 *  - pad / pier / deadman: "soil cone" (frustum) method. Resistance = self-weight
 *    of the concrete + weight of the inverted soil cone bounded by faces rising at
 *    the friction angle φ from the footing edge. Deeper + wider + heavier soil =
 *    more uplift resistance. Piers add side friction.
 *
 *  - rock-anchor: grout-to-rock bond over the bonded length:
 *    Q = π·D·L·τ_bond, τ_bond ≈ 500 kPa–1 MPa for competent rock; we scale it
 *    with soil.cohesion as a stand-in for rock quality.
 */
export function upliftCapacity(f: FoundationSpec, soil: SoilParams): number {
  if (f.type === "rock-anchor") {
    // grout/rock bond; use cohesion as rock-quality proxy (rock preset c=500 kPa)
    const tauBond = Math.max(200e3, soil.cohesion); // Pa, floor 200 kPa
    return Math.PI * f.diameter * f.depth * tauBond;
  }

  const W = selfWeight(f); // concrete always helps

  if (f.type === "pier") {
    // shaft: self-weight + full side friction resists pullout
    return W + pileFriction(f, soil);
  }

  // pad / deadman: inverted soil-cone (frustum) weight above the footing.
  // Cone spreads outward at angle φ over the embedment depth.
  const phi = (soil.phiDeg * Math.PI) / 180;
  const spread = f.depth * Math.tan(phi); // horizontal growth of the frustum
  const halfB = f.width * 0.5;
  const topHalf = halfB + spread;
  // Volume of a square frustum: (h/3)(A1 + A2 + sqrt(A1·A2))
  const A1 = f.width * f.width;
  const A2 = (2 * topHalf) * (2 * topHalf);
  const frustumVol = (f.depth / 3) * (A1 + A2 + Math.sqrt(A1 * A2));
  const soilCone = frustumVol * soil.gamma; // gamma already a weight density (N/m^3)
  // Clays with c but φ=0: no cone spread, so add a shear term on the vertical
  // prism faces instead (perimeter · depth · c).
  const shear = 4 * f.width * f.depth * soil.cohesion;
  return W + soilCone + shear;
}

/**
 * SLIDING capacity — max horizontal base shear the foundation resists before it
 * slides (N). Friction on the base (μ·N, N = self-weight + download) plus a
 * passive-earth term on the embedded face:
 *   H_res = μ·(W + P_down) + 0.5·Kp·γ·depth^2 · width
 * Kp = tan²(45 + φ/2) passive coefficient. [TEP friction factor 0.5]
 *
 * `downForce` is the current vertical download on this foundation (N, ≥0).
 */
export function slidingCapacity(
  f: FoundationSpec,
  soil: SoilParams,
  downForce: number,
): number {
  const phi = (soil.phiDeg * Math.PI) / 180;
  const N = selfWeight(f) + Math.max(0, downForce);
  const friction = soil.frictionFactor * N;
  const Kp = Math.pow(Math.tan(Math.PI / 4 + phi / 2), 2);
  const face = f.type === "pier" || f.type === "rock-anchor" ? f.diameter : f.width;
  const passive = 0.5 * Kp * soil.gamma * f.depth * f.depth * face;
  return friction + passive;
}

/**
 * OVERTURNING resisting moment (N·m) about the footing edge — for a whole-tower
 * or whole-monopole check where one foundation carries the base moment (piers /
 * monopole shafts). Resisting moment = (self-weight + vertical dead load + weight
 * of overburden soil) acting at the footing half-width lever arm.
 *   M_resist = (W + P_down) · (width/2)          [HM restoring-moment check]
 * Compare against the applied base overturning moment; codes want FS ≥ 1.5. [TIA/HM]
 *
 * For a multi-leg lattice the overturning is better handled as per-leg uplift
 * (see `upliftCapacity`) — the windward leg tension is M_over / base_width.
 */
export function overturningResist(
  f: FoundationSpec,
  downForce: number,
  soil: SoilParams,
): number {
  const halfArm = (f.type === "pier" || f.type === "rock-anchor" ? f.diameter : f.width) * 0.5;
  // overburden soil sitting on the pad shoulders also resists (approx: pad area
  // × depth × soil weight, only counted for pads/deadmen).
  const overburden =
    f.type === "pad" || f.type === "deadman"
      ? f.width * f.width * f.depth * soil.gamma * 0.5 // 50% of soil weight [HM]
      : 0;
  const N = selfWeight(f) + Math.max(0, downForce) + overburden;
  return N * halfArm;
}

// ---------------------------------------------------------------------------
// Safety factors (TIA-222 / geotech practice)
// ---------------------------------------------------------------------------

/** Target minimum safety factors per failure mode. [TIA/HM] */
export const SAFETY_FACTORS = {
  bearing: 2.0, // qa already ~SF 2–3 vs ultimate; keep 2.0 on capacity
  overturning: 1.5, // classic OTM factor [HM 2.25 > 1.5]
  sliding: 1.5,
  uplift: 1.5, // 2.0 common for anchors; 1.5 for game headroom
} as const;

export type FailureMode = keyof typeof SAFETY_FACTORS;

/**
 * Resolved capacity bundle for one foundation node — what the solver needs to
 * decide whether a fixed base/anchor should yield. All in N (moments N·m).
 * `downForce` is the working vertical load used to size sliding/overturning.
 */
export interface FoundationCapacity {
  bearing: number; // N — max download
  uplift: number; // N — max tension before pullout
  sliding: number; // N — max base shear
  overturning: number; // N·m — max resisting moment
  selfWeight: number; // N — concrete dead weight (also the release threshold help)
}

/** Compute the full capacity bundle for a foundation on a given soil. */
export function foundationCapacity(
  f: FoundationSpec,
  soil: SoilParams,
  downForce = 0,
): FoundationCapacity {
  return {
    bearing: bearingCapacity(f, soil),
    uplift: upliftCapacity(f, soil),
    sliding: slidingCapacity(f, soil, downForce),
    overturning: overturningResist(f, downForce, soil),
    selfWeight: selfWeight(f),
  };
}

/**
 * Utilization of a foundation node under a reaction. `reaction` uses the sign
 * convention: vertical +down (compression into soil), -up (uplift/tension);
 * `shear` is the horizontal reaction magnitude. Returns the governing (largest)
 * utilization across bearing / uplift / sliding; ≥ 1 means the foundation fails
 * (releases). Mirrors how member `utilization()` works in model.ts.
 */
export function foundationUtilization(
  cap: FoundationCapacity,
  verticalDown: number,
  shear: number,
): number {
  const bearingUtil = verticalDown > 0 ? (verticalDown * SAFETY_FACTORS.bearing) / cap.bearing : 0;
  const upliftUtil = verticalDown < 0 ? (-verticalDown * SAFETY_FACTORS.uplift) / cap.uplift : 0;
  const slidingUtil = cap.sliding > 0 ? (Math.abs(shear) * SAFETY_FACTORS.sliding) / cap.sliding : 0;
  return Math.max(bearingUtil, upliftUtil, slidingUtil);
}

/** Which geotech check governs (for explaining WHY a footing/anchor let go). */
export function foundationFailureMode(
  cap: FoundationCapacity,
  verticalDown: number,
  shear: number,
): "bearing" | "uplift" | "sliding" {
  const bearingUtil = verticalDown > 0 ? (verticalDown * SAFETY_FACTORS.bearing) / cap.bearing : 0;
  const upliftUtil = verticalDown < 0 ? (-verticalDown * SAFETY_FACTORS.uplift) / cap.uplift : 0;
  const slidingUtil = cap.sliding > 0 ? (Math.abs(shear) * SAFETY_FACTORS.sliding) / cap.sliding : 0;
  if (upliftUtil >= bearingUtil && upliftUtil >= slidingUtil) return "uplift";
  if (slidingUtil >= bearingUtil) return "sliding";
  return "bearing";
}
